import React, { useEffect, useState } from 'react';
import { BrandLogoIcon } from './BrandLogoIcon';
import { HERO_STATUS_CYCLE } from '../config/productCopy';

const PII_PLACEHOLDERS = [
  { label: '[Name]', icon: 'user' },
  { label: '[Date of Birth]', icon: 'calendar' },
  { label: '[Student ID]', icon: 'hash' },
  { label: '[Address]', icon: 'pin' },
  { label: '[Parent / Guardian]', icon: 'users' },
] as const;

function PlaceholderIcon({ type }: { type: (typeof PII_PLACEHOLDERS)[number]['icon'] }) {
  const common = 'w-3.5 h-3.5 text-slate-400 flex-shrink-0';
  switch (type) {
    case 'user':
      return (
        <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'hash':
      return (
        <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      );
    case 'pin':
      return (
        <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'users':
      return (
        <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      );
  }
}

/**
 * Illustrative homepage visualization only — does not process real documents.
 */
export const PreparationFlowVisualization: React.FC = () => {
  const [statusIndex, setStatusIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setStatusIndex(HERO_STATUS_CYCLE.length - 1);
      return;
    }
    const id = window.setInterval(() => {
      setStatusIndex((i) => (i + 1) % HERO_STATUS_CYCLE.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [reducedMotion]);

  const status = HERO_STATUS_CYCLE[statusIndex];

  return (
    <div
      className="@container w-full min-w-0"
      role="img"
      aria-label="Illustration of preparing an educational document for AI use"
    >
      {/*
        Stack by default; switch to a horizontal Original → Prepare → Prepared
        flow only when this container is wide enough. Uses container queries so
        the graphic still fits when the hero is a 2-column layout (~half width).
      */}
      <div className="flex min-w-0 flex-col gap-4 @[26rem]:flex-row @[26rem]:items-stretch @[26rem]:gap-2">
        {/* Original Document */}
        <div className="min-w-0 flex-1 basis-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-md shadow-slate-200/60 sm:p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Original Document
          </p>
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-12 w-10 flex-shrink-0 items-center justify-center rounded-md border border-red-100 bg-red-50">
              <span className="text-[10px] font-bold text-red-600">PDF</span>
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">IEP_Report.pdf</p>
              <p className="text-xs text-slate-500">42 pages</p>
            </div>
          </div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Potential PII Detected
          </p>
          <ul className="space-y-1.5">
            {PII_PLACEHOLDERS.map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-sm text-slate-700"
              >
                <PlaceholderIcon type={item.icon} />
                <span className="min-w-0 break-words font-mono text-xs sm:text-sm">{item.label}</span>
              </li>
            ))}
            <li className="pl-2 pt-0.5 text-xs text-slate-400">… and more</li>
          </ul>
        </div>

        {/* Center preparation layer */}
        <div className="flex w-full shrink-0 flex-row items-center justify-center gap-2 @[26rem]:w-[6.75rem] @[26rem]:flex-col @[26rem]:px-0">
          <div className="hidden w-px flex-1 bg-gradient-to-b from-transparent via-indigo-200 to-transparent @[26rem]:block" />
          <div className="relative flex h-24 w-24 items-center justify-center sm:h-28 sm:w-28">
            <div
              className={`absolute inset-0 rounded-full bg-indigo-100/80 ${reducedMotion ? '' : 'prep-pulse-ring'}`}
            />
            <div
              className={`absolute inset-1 rounded-full border border-dashed border-indigo-300/70 ${
                reducedMotion ? '' : 'prep-orbit-ring'
              }`}
              aria-hidden
            >
              <span className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-400" />
              <span className="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rounded-full bg-emerald-400" />
              <span className="absolute top-1/2 left-0 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-300" />
              <span className="absolute top-1/2 right-0 h-1.5 w-1.5 translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500" />
            </div>
            <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-100 bg-white shadow-md sm:h-16 sm:w-16">
              <BrandLogoIcon className="h-10 w-10 sm:h-11 sm:w-11" />
            </div>
          </div>
          <div className="max-w-[9rem] text-center">
            <p className="text-xs font-bold text-indigo-800">Redact AI</p>
            <p
              className="mt-0.5 flex min-h-[2.5rem] items-center justify-center text-[11px] font-medium leading-snug text-emerald-700"
              aria-live="polite"
            >
              {status}
            </p>
          </div>
          <div className="hidden w-px flex-1 bg-gradient-to-b from-transparent via-emerald-200 to-transparent @[26rem]:block" />
          {/* Stacked-flow cue when vertical */}
          <div className="flex items-center gap-1 text-indigo-300 @[26rem]:hidden" aria-hidden>
            <span className={`inline-block h-1.5 w-1.5 rounded-full bg-indigo-400 ${reducedMotion ? '' : 'prep-flow-dot'}`} />
            <span className={`inline-block h-1.5 w-1.5 rounded-full bg-indigo-300 ${reducedMotion ? '' : 'prep-flow-dot prep-flow-dot-delay'}`} />
            <span className={`inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 ${reducedMotion ? '' : 'prep-flow-dot prep-flow-dot-delay-2'}`} />
          </div>
        </div>

        {/* Prepared Document */}
        <div className="min-w-0 flex-1 basis-0 rounded-2xl border border-emerald-200 bg-white p-4 shadow-md shadow-emerald-100/50 sm:p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Prepared Document
          </p>
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-12 w-10 flex-shrink-0 items-center justify-center rounded-md border border-emerald-100 bg-emerald-50">
              <span className="text-[10px] font-bold text-emerald-700">PDF</span>
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">IEP_Report_Prepared.pdf</p>
              <p className="text-xs text-slate-500">42 pages</p>
            </div>
          </div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
            Preparation Summary
          </p>
          <ul className="mb-4 space-y-1.5 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <span className="font-bold text-emerald-600">✓</span>
              <span className="min-w-0 leading-snug">PII scan completed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-emerald-600">✓</span>
              <span className="min-w-0 leading-snug">Redactions applied</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-emerald-600">✓</span>
              <span className="min-w-0 leading-snug">Original page count preserved</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-emerald-600">✓</span>
              <span className="min-w-0 leading-snug">Prepared file generated</span>
            </li>
          </ul>
          <div className="flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3">
            <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug text-emerald-900">42 of 42 pages prepared</p>
              <p className="text-xs font-medium text-emerald-700">Ready for Review</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
